import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { PlusIcon } from "lucide-react";
import Chat, { ChatRef } from "./Chat";

const StudioOverview: React.FC = () => {
  const navigate = useNavigate();
  const [hasMessages, setHasMessages] = useState(false);
  const [resetTrigger, setResetTrigger] = useState(0);
  const chatRef = useRef<ChatRef>(null);

  const handleNewConversation = () => {
    setResetTrigger(prev => prev + 1);
    setHasMessages(false);
  };

  // Override parent LayoutBody styles for full-height chat
  useEffect(() => {
    const layoutBody = document.querySelector("[class*=\"flex-1\"][class*=\"overflow-auto\"]");
    if (layoutBody) {
      (layoutBody as HTMLElement).style.overflow = "hidden";
      (layoutBody as HTMLElement).style.padding = "0";
    }
    return () => {
      if (layoutBody) {
        (layoutBody as HTMLElement).style.overflow = "";
        (layoutBody as HTMLElement).style.padding = "";
      }
    };
  }, []);

  return (
    <div className="h-full bg-background flex flex-col">
      <div className="max-w-6xl mx-auto px-8 py-8 w-full flex-shrink-0">
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Home
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <button 
                    onClick={() => navigate("/forge")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Forge
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">Studio</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                  Studio
                </h1>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manually test and interact with your agents and models
                </p>
              </div>
              {hasMessages && (
                <Button onClick={handleNewConversation} className="gap-2">
                  <PlusIcon className="w-4 h-4" />
                  New Conversation
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Chat Interface - Takes remaining space */}
      <div className="flex-1 min-h-0">
        <Chat 
          ref={chatRef}
          onHasMessagesChange={setHasMessages}
          resetTrigger={resetTrigger}
          streaming={true}
          evaluations={false}
          autocorrect={false}
          retrieveContext={false}
          temperature={0.7}
        />
      </div>
    </div>
  );
};

export default StudioOverview;

